// ============================================================
// 📄 GERADOR DO LAUDO APAC (B211) — imagem de média/alta complexidade
// ============================================================
// Reproduz, em HTML imprimível FIEL ao formulário oficial do Ministério da
// Saúde, o "LAUDO PARA SOLICITAÇÃO/AUTORIZAÇÃO DE PROCEDIMENTO AMBULATORIAL"
// (APAC) — 52 campos numerados. Exigido para imagem de média/alta complexidade
// (TC, RM, cintilografia, PET-CT). US/endoscopia/radiografia NÃO exigem.
//
// Diferente da família carga viral: VÁRIOS exames de imagem de um pedido entram
// num ÚNICO laudo (procedimento principal + até 5 secundários). O médico
// solicitante preenche, pelo modal, apenas: descrição do diagnóstico (33),
// CID10 principal (34) e observações/indicação do exame (37). O restante é
// pré-preenchido do prontuário (paciente) e do cadastro (solicitante). Os
// blocos de AUTORIZAÇÃO (43–50) e ESTABELECIMENTO EXECUTANTE (51–52) saem em
// branco — são preenchidos pela regulação/executante.
//
// 100% client-side, sem biblioteca de PDF e sem logar conteúdo clínico (LGPD).

import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import { Paciente } from "../../../types";
import { abrirPDFEmNovaJanela } from "./gerarPDF";
import { escapeHtml, formatarDataBR } from "./gerarFichaGAL";
import { SolicitanteCargaViral } from "./gerarFichaCargaViral";
import susLogoBase64 from "./susLogoBase64";

// Reusa o solicitante da carga viral (mesmos campos: profissional + unidade).
export type SolicitanteAPAC = SolicitanteCargaViral;

// Procedimento solicitado (exame de imagem). O código SIGTAP do SUS não está no
// catálogo (que é TUSS) — sai em branco para a regulação preencher; o nome do
// exame é o dado útil ao solicitante.
export interface ProcedimentoAPAC {
  codigo?: string; // campo 15/18/.../30 (SIGTAP) — em branco por ora
  nome: string; // campo 16/19/.../31 — nome do exame
  quantidade?: string; // campo 17/20/.../32 — default "1"
}

// Dados preenchidos pelo médico no modal (persistidos junto ao pedido).
export interface DadosAPAC {
  dataSolicitacao: string; // dd/mm/aaaa (campo 39)
  diagnostico: string; // campo 33 — descrição do diagnóstico
  cid10Principal: string; // campo 34
  cid10Secundario?: string; // campo 35 (opcional)
  observacoes: string; // campo 37 — indicação do exame
}

// ------------------------------------------------------------
// CÉLULA DO FORMULÁRIO (grade de 12 colunas) — mesmo padrão visual da carga viral
// ------------------------------------------------------------
interface OpcoesCelula {
  span?: number;
  obrig?: boolean;
  alto?: boolean; // campo de texto livre mais alto (diagnóstico)
  obs?: boolean; // campo de observações (bem maior, igual ao original)
  assinatura?: boolean; // campo de assinatura/carimbo (alto p/ assinar; AI_118)
}

// Rótulo no topo, CENTRALIZADO no formato "N - LABEL" (fiel ao laudo oficial),
// valor preenchido logo abaixo.
function celula(
  num: string,
  rotulo: string,
  valor: string,
  opts: OpcoesCelula = {}
): string {
  const { span = 1, obrig = false, alto = false, obs = false, assinatura = false } = opts;
  const extra = obs
    ? " ap-obs"
    : alto
    ? " ap-alto"
    : assinatura
    ? " ap-assinatura"
    : "";
  const rot = num ? `${num} - ${rotulo}` : rotulo;
  return `
    <div class="ap-campo${extra}" style="grid-column: span ${span};">
      <div class="ap-topo">${rot}${
    obrig ? '<span class="ap-ob">*</span>' : ""
  }</div>
      <div class="ap-fill">${valor || "&nbsp;"}</div>
    </div>`;
}

const barra = (titulo: string): string =>
  `<div class="ap-barra"><span>${escapeHtml(titulo)}</span></div>`;

const linha = (campos: string): string => `<div class="ap-linha">${campos}</div>`;

// Marca uma das duas opções (CNS/CPF, Masc/Fem) conforme `marcado`.
const caixa = (texto: string, marcado: boolean): string =>
  `(${marcado ? "X" : "&nbsp;&nbsp;"}) ${escapeHtml(texto)}`;

// ------------------------------------------------------------
// MIOLO DO LAUDO (sem <html>/<style>/@page)
// ------------------------------------------------------------
function corpoAPAC(
  paciente: Paciente,
  solicitante: SolicitanteAPAC,
  dados: DadosAPAC,
  procedimentos: ProcedimentoAPAC[],
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 42 (assinatura do
  // solicitante), só no PDF assinado. Sem selo = campo em branco (assinar à mão).
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub || "";
  const endereco = [
    paciente.logradouro,
    paciente.numero,
    paciente.bairro,
  ]
    .filter(Boolean)
    .join(", ");
  const telefone =
    telefoneVigente(paciente) || paciente.telefone2 || "";

  const principal = procedimentos[0];
  const secundarios = procedimentos.slice(1, 6); // até 5 secundários (campos 18–32)

  // Linhas de procedimentos secundários (campos 18–32) — sempre 5 linhas (em
  // branco se não houver), fiéis ao formulário oficial.
  const numsSec = [
    ["18", "19", "20"],
    ["21", "22", "23"],
    ["24", "25", "26"],
    ["27", "28", "29"],
    ["30", "31", "32"],
  ];
  const linhasSecundarias = numsSec
    .map((nums, i) => {
      const p = secundarios[i];
      return linha(
        celula(nums[0], "Código do procedimento secundário", p?.codigo || "", {
          span: 3,
        }) +
          celula(nums[1], "Nome do procedimento secundário", escapeHtml(p?.nome || ""), {
            span: 7,
          }) +
          celula(nums[2], "Qtde.", p ? p.quantidade || "1" : "", { span: 2 })
      );
    })
    .join("");

  // Documento do profissional: prioriza o CNS (preferência de Leonardo);
  // usa o CPF só como fallback se não houver CNS cadastrado.
  const ehCNS = !!solicitante.cnsProfissional;
  const docProfissional =
    solicitante.cnsProfissional || solicitante.cpfProfissional || "";

  return `
  <div class="ap">
    <div class="ap-cab">
      <div class="ap-cab-logo">
        <img src="${susLogoBase64}" alt="Logo do SUS" />
        <div class="ap-cab-min">
          <span><b>Sistema</b><br/>Único de<br/>Saúde</span>
          <span><b>Ministério</b><br/>da<br/>Saúde</span>
        </div>
      </div>
      <div class="ap-titulo">LAUDO PARA SOLICITAÇÃO/AUTORIZAÇÃO DE<br/>PROCEDIMENTO AMBULATORIAL</div>
      <div class="ap-fls">fls.1/2</div>
    </div>

    ${barra("IDENTIFICAÇÃO DO ESTABELECIMENTO DE SAÚDE (SOLICITANTE)")}
    ${linha(
      celula("1", "Nome do estabelecimento de saúde solicitante", escapeHtml(solicitante.unidadeSaude || ""), { span: 9, obrig: true }) +
        celula("2", "CNES", escapeHtml(solicitante.cnes || ""), { span: 3, obrig: true })
    )}

    ${barra("IDENTIFICAÇÃO DO PACIENTE")}
    ${linha(
      celula("3", "Nome do paciente", escapeHtml(paciente.nome), { span: 8, obrig: true }) +
        celula("4", "Nº do prontuário", "", { span: 4 })
    )}
    ${linha(
      celula("5", "Cartão Nacional de Saúde (CNS)", escapeHtml(cnsPaciente), { span: 5, obrig: true }) +
        celula("6", "Data de nascimento", formatarDataBR(paciente.dataNascimento), { span: 3, obrig: true }) +
        celula("7", "Sexo", `${caixa("Masc.", paciente.sexo === "M")} &nbsp; ${caixa("Fem.", paciente.sexo === "F")}`, { span: 4, obrig: true })
    )}
    ${linha(
      celula("8", "Nome da mãe ou responsável", escapeHtml(paciente.nomeMae || ""), { span: 8 }) +
        celula("9", "Telefone de contato", escapeHtml(telefone), { span: 4 })
    )}
    ${linha(
      celula("10", "Endereço (rua, nº, bairro)", escapeHtml(endereco), { span: 8 }) +
        celula("11", "Município de residência", escapeHtml(paciente.cidade || ""), { span: 4 })
    )}
    ${linha(
      celula("12", "Cód. IBGE município", "", { span: 5 }) +
        celula("13", "UF", escapeHtml(paciente.estado || ""), { span: 2 }) +
        celula("14", "CEP", escapeHtml(paciente.cep || ""), { span: 5 })
    )}

    ${barra("PROCEDIMENTO SOLICITADO")}
    ${linha(
      celula("15", "Código do procedimento principal", principal?.codigo || "", { span: 3 }) +
        celula("16", "Nome do procedimento principal", escapeHtml(principal?.nome || ""), { span: 7, obrig: true }) +
        celula("17", "Qtde.", principal ? principal.quantidade || "1" : "", { span: 2 })
    )}
    ${barra("PROCEDIMENTO(S) SECUNDÁRIO(S)")}
    ${linhasSecundarias}

    ${barra("JUSTIFICATIVA DO(S) PROCEDIMENTO(S) SOLICITADO(S)")}
    ${linha(
      celula("33", "Descrição do diagnóstico", escapeHtml(dados.diagnostico), { span: 6, obrig: true }) +
        celula("34", "CID10 principal", escapeHtml(dados.cid10Principal), { span: 2, obrig: true }) +
        celula("35", "CID10 secundário", escapeHtml(dados.cid10Secundario || ""), { span: 2 }) +
        celula("36", "CID10 causas associadas", "", { span: 2 })
    )}
    ${linha(
      celula("37", "Observações", escapeHtml(dados.observacoes), { span: 12, obs: true })
    )}

    ${barra("SOLICITAÇÃO")}
    ${linha(
      celula("38", "Nome do profissional solicitante", escapeHtml(solicitante.nomeProfissional), { span: 8, obrig: true }) +
        celula("39", "Data da solicitação", escapeHtml(dados.dataSolicitacao), { span: 4, obrig: true })
    )}
    ${linha(
      celula("40", "Documento", `${caixa("CNS", ehCNS)} &nbsp; ${caixa("CPF", !ehCNS && !!docProfissional)}`, { span: 3 }) +
        celula("41", "Nº documento (CNS/CPF) do profissional solicitante", escapeHtml(docProfissional), { span: 5, obrig: true }) +
        celula("42", "Assinatura e carimbo (nº registro do conselho)", selo || "", { span: 4, assinatura: true })
    )}

    ${barra("AUTORIZAÇÃO")}
    ${linha(
      celula("43", "Nome do profissional autorizador", "", { span: 8 }) +
        celula("44", "Cód. órgão emissor", "", { span: 4 })
    )}
    ${linha(
      celula("45", "Documento", "(&nbsp;&nbsp;) CNS &nbsp; (&nbsp;&nbsp;) CPF", { span: 3 }) +
        celula("46", "Nº documento (CNS/CPF) do profissional autorizador", "", { span: 5 }) +
        celula("47", "Data da autorização", "", { span: 4 })
    )}
    ${linha(
      celula("48", "Assinatura e carimbo (nº registro do conselho)", "", { span: 6, assinatura: true }) +
        celula("49", "Nº da autorização (APAC)", "", { span: 3 }) +
        celula("50", "Período de validade da APAC", "", { span: 3 })
    )}

    ${barra("IDENTIFICAÇÃO DO ESTABELECIMENTO DE SAÚDE (EXECUTANTE)")}
    ${linha(
      celula("51", "Nome fantasia do estabelecimento de saúde executante", "", { span: 9 }) +
        celula("52", "CNES", "", { span: 3 })
    )}

    <div class="ap-rodape">Laudo para Solicitação/Autorização de Procedimento Ambulatorial (APAC) — Ministério da Saúde. Campos com * são obrigatórios.</div>
  </div>`;
}

// ------------------------------------------------------------
// ESTILOS
// ------------------------------------------------------------
// AI_118: fontes e alturas aumentadas (legibilidade — Leonardo). Há folga na
// folha (fls.1/2). Campos de assinatura/carimbo (42/48) ganham altura própria.
// AI_118 (2ª rodada): fonte um pouco maior + valor CENTRALIZado na célula (a
// célula virou flex-coluna; o valor cresce e centra V+H). Campos de texto longo
// (diagnóstico/observações) seguem alinhados à esquerda (parágrafo centralizado
// fica ruim).
const ESTILOS_APAC = `
  .ap { font-family: Arial, Helvetica, sans-serif; color: #000; font-size: 10px; line-height: 1.18; border: 1.5px solid #000; }
  .ap-cab { display: flex; align-items: stretch; border-bottom: 1.5px solid #000; }
  .ap-cab-logo { display: flex; align-items: center; gap: 6px; padding: 4px 8px; border-right: 1.5px solid #000; }
  .ap-cab-logo img { width: 40px; height: auto; display: block; }
  .ap-cab-min { display: flex; gap: 8px; font-size: 7.5px; line-height: 1.05; }
  .ap-titulo { flex: 1; display: flex; align-items: center; justify-content: center; text-align: center; font-weight: 700; font-style: italic; font-size: 13px; letter-spacing: .3px; padding: 4px; }
  .ap-fls { padding: 4px 8px; font-weight: 700; font-style: italic; font-size: 9px; align-self: flex-start; }
  .ap-barra { background: #000; color: #fff; text-align: center; font-weight: 700; font-size: 11px; letter-spacing: .4px; padding: 3px 4px; }
  .ap-linha { display: grid; grid-template-columns: repeat(12, 1fr); }
  .ap-campo { border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 1px 4px 3px; min-height: 31px; display: flex; flex-direction: column; }
  .ap-campo:last-child { border-right: none; }
  .ap-alto { min-height: 46px; }
  .ap-obs { min-height: 72px; }
  .ap-assinatura { min-height: 62px; }
  .ap-topo { text-align: center; font-size: 9px; color: #000; text-transform: uppercase; letter-spacing: .2px; }
  .ap-ob { color: #b00; margin-left: 1px; }
  .ap-fill { flex: 1; margin-top: 1px; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 12.5px; font-weight: 600; white-space: pre-wrap; word-break: break-word; }
  .ap-alto .ap-fill, .ap-obs .ap-fill { display: block; text-align: left; }
  .ap-rodape { padding: 4px 6px; font-size: 8px; color: #333; border-top: 1.5px solid #000; }
`;

// ------------------------------------------------------------
// API PÚBLICA
// ------------------------------------------------------------
/** Fragmento p/ embutir no MESMO documento do pedido de exames (folha extra). */
export function montarFragmentoAPAC(
  paciente: Paciente,
  solicitante: SolicitanteAPAC,
  dados: DadosAPAC,
  procedimentos: ProcedimentoAPAC[],
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 42, só no PDF assinado.
  selo?: string
): { estilos: string; corpo: string } {
  const estilos = `
    @page apac { size: A4 portrait; margin: 5mm; }
    .folha-apac { page: apac; page-break-before: always; }
    ${ESTILOS_APAC}
  `;
  const corpo = `<div class="folha-apac">${corpoAPAC(
    paciente,
    solicitante,
    dados,
    procedimentos,
    selo
  )}</div>`;
  return { estilos, corpo };
}

/** Documento AUTÔNOMO do laudo APAC (sem o pedido comum). */
function montarHTML(
  paciente: Paciente,
  solicitante: SolicitanteAPAC,
  dados: DadosAPAC,
  procedimentos: ProcedimentoAPAC[]
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Laudo APAC — ${escapeHtml(paciente.nome)}</title>
  <style>
    @page { size: A4 portrait; margin: 5mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; background: #e9e9e9; }
    .barra-botoes { text-align: center; padding: 14px; }
    .ap { width: 200mm; margin: 0 auto 20px; background: #fff; }
    ${ESTILOS_APAC}
    @media print {
      body { background: #fff; }
      .ap { width: auto; margin: 0; }
      .barra-botoes { display: none; }
    }
  </style>
</head>
<body>
  <div class="barra-botoes">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir / Salvar PDF
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${corpoAPAC(paciente, solicitante, dados, procedimentos)}
</body>
</html>`;
}

export function gerarFichaAPAC(
  paciente: Paciente,
  solicitante: SolicitanteAPAC,
  dados: DadosAPAC,
  procedimentos: ProcedimentoAPAC[]
): void {
  abrirPDFEmNovaJanela(montarHTML(paciente, solicitante, dados, procedimentos));
}
